import React, { useEffect, useMemo, useState, useCallback } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import {
  episode_detail_admin,
  episode_watch_by_customer,
  episode_watch_by_coin_customer_list,
} from "../../../actions/WebSeries/episode";
import { useLocation } from "react-router-dom";

const ROW_OPTIONS = [5, 10, 20, 50];

const unwrap = (r) => r?.data?.data || r?.data || r || {};

const EpisodeDetail = ({
  navigate,
  episode_detail_admin,
  episode_watch_by_customer,
  episode_watch_by_coin_customer_list,
}) => {
  const location = useLocation();
  const episodeId = location?.state?.id;
  console.log(location, "location")

  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(true);

  const [watchers, setWatchers] = useState([]);
  const [watchCount, setWatchCount] = useState(0);
  const [watchLoading, setWatchLoading] = useState(true);
  const [watchPage, setWatchPage] = useState(1);
  const [watchRows, setWatchRows] = useState(10);

  const [coinUsers, setCoinUsers] = useState([]);
  const [coinCount, setCoinCount] = useState(0);
  const [coinLoading, setCoinLoading] = useState(false);
  const [coinPage, setCoinPage] = useState(1);
  const [coinRows, setCoinRows] = useState(10);

  const isPaid = String(detail?.episode_access || "").toLowerCase() === "paid";

  const fetchDetail = useCallback(async () => {
    if (!episodeId) return;
    setDetailLoading(true);
    try {
      const res = await episode_detail_admin({ id: episodeId });
      const payload = unwrap(res);
      console.log(payload, res,"episode detail payload");
      setDetail(payload?.data || payload);
    } catch (e) {
      console.error("episode detail error", e);
    } finally {
      setDetailLoading(false);
    }
  }, [episodeId, episode_detail_admin]);

  const fetchWatchers = useCallback(async () => {
    if (!episodeId) return;
    setWatchLoading(true);
    try {
      const res = await episode_watch_by_customer({
        episode_id: episodeId,
        page_number: watchPage,
        row_size: watchRows,
      });
      const payload = unwrap(res);
      setWatchers(payload?.episode_watch_by || []);
      setWatchCount(payload?.episode_watch_by_count || 0);
    } catch (e) {
      console.error("watchers error", e);
    } finally {
      setWatchLoading(false);
    }
  }, [episodeId, watchPage, watchRows, episode_watch_by_customer]);

  const fetchCoinUsers = useCallback(async () => {
    if (!episodeId || !isPaid) return;
    setCoinLoading(true);
    try {
      const res = await episode_watch_by_coin_customer_list({
        episode_id: episodeId,
        page_number: coinPage,
        row_size: coinRows,
      });
      const payload = unwrap(res);
      setCoinUsers(payload?.episode_watch_by_coin || []);
      setCoinCount(payload?.episode_watch_by_coin_count || 0);
    } catch (e) {
      console.error("coin users error", e);
    } finally {
      setCoinLoading(false);
    }
  }, [episodeId, coinPage, coinRows, isPaid, episode_watch_by_coin_customer_list]);

  useEffect(() => { fetchDetail(); }, [fetchDetail]);
  useEffect(() => { fetchWatchers(); }, [fetchWatchers]);
  useEffect(() => { if (isPaid) fetchCoinUsers(); }, [fetchCoinUsers, isPaid]);

  useEffect(() => { setCoinPage(1); }, [coinRows]);
  useEffect(() => { setWatchPage(1); }, [watchRows]);

  const totalWatchPages = useMemo(() => Math.max(1, Math.ceil(watchCount / watchRows)), [watchCount, watchRows]);
  const totalCoinPages = useMemo(() => Math.max(1, Math.ceil(coinCount / coinRows)), [coinCount, coinRows]);

  const formatDate = (value) => {
    if (!value) return "—";
    try {
      return new Date(value).toLocaleString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return value;
    }
  };

  const displayName = (u) => u?.name || u?.mobileNumber || u?.email || `Customer #${u?.id}`;
  const initials = (u) => {
    const base = u?.name || u?.mobileNumber || u?.email || "CU";
    return String(base).replace(/[^A-Za-z0-9 ]/g, "").split(" ").filter(Boolean).slice(0,2).map(x => x[0]).join("").toUpperCase() || "CU";
  };

  if (!episodeId) {
    return (
      <div className="ep-page-root">
        <style>{styles}</style>
        <div className="ep-empty-state">Episode ID not found. Please open this page from the series detail screen.</div>
      </div>
    );
  }

  return (
    <div className="ep-page-root">
      <style>{styles}</style>

      <div className="ep-topbar">
        <div>
          <h1 className="ep-page-title">Episode Detail</h1>
          <p className="ep-page-sub">Watch analytics, access type and customer activity for this episode</p>
        </div>
        <button className="ep-back-btn" onClick={() => navigate ? navigate(-1) : window.history.back()}>
          <span>←</span> Back
        </button>
      </div>

      <div className="ep-hero-grid">
        <div className="ep-side-card">
          <div className="ep-thumb-block">
            <div className="ep-thumb-inner">
              <div className="ep-episode-no">EP {detail?.episode_number || "—"}</div>
              <div className="ep-thumb-title">{detailLoading ? "Loading..." : detail?.title || "Untitled Episode"}</div>
              <div className={`ep-access-pill ${isPaid ? 'paid' : 'free'}`}>
                {detail?.episode_access || "—"}
                {isPaid && detail?.episode_coin ? ` (${detail.episode_coin} Coins)` : ""}
              </div>
            </div>
          </div>

          <div className="ep-kpi-box">
            <div className="ep-kpi-label">Total Watch Customers</div>
            <div className="ep-kpi-value">{watchCount}</div>
          </div>
        </div>

        <div className="ep-main-card">
          <div className="ep-main-head">Episode Information</div>
          <div className="ep-main-body">
            <div className="ep-info-grid">
              <Info label="Title" value={detail?.title} loading={detailLoading} />
              <Info label="Episode Number" value={detail?.episode_number} loading={detailLoading} />
              <Info label="Duration" value={detail?.duration} loading={detailLoading} />
              <Info label="Status" value={<span className={`ep-status ${String(detail?.status).toLowerCase() === 'active' ? 'active' : 'inactive'}`}>{detail?.status || '—'}</span>} loading={detailLoading} />
              <Info label="Access" value={detail?.episode_access} loading={detailLoading} />
              <Info label="Coin Required" value={isPaid ? (detail?.episode_coin || 0) : 'Free Episode'} loading={detailLoading} />
              <Info label="Created At" value={detail?.created_at} loading={detailLoading} />
              {/* <Info label="Series ID" value={detail?.series} loading={detailLoading} /> */}
              {/* <Info label="Season ID" value={detail?.season} loading={detailLoading} /> */}
              <Info label="M3U8 Link" value={detail?.m3u8_link ? <a href={detail.m3u8_link} target="_blank" rel="noreferrer" className="ep-link">Open Stream Link</a> : '—'} loading={detailLoading} />
              <Info label="MP4 Link" value={detail?.mp4_link ? <a href={detail.mp4_link} target="_blank" rel="noreferrer" className="ep-link">Open Download Link</a> : '—'} loading={detailLoading} />
            </div>
          </div>
        </div>
      </div>

      <div className="ep-stats-row">
        <StatCard title="Total Watchers" value={watchCount} sub="Customers who opened this episode" color="#3b82f6" />
        <StatCard title="Access Type" value={detail?.episode_access || '—'} sub={isPaid ? `${detail?.episode_coin || 0} coins required` : 'No coin payment needed'} color={isPaid ? '#f59e0b' : '#10b981'} />
        <StatCard title="Coin Unlock Users" value={isPaid ? coinCount : 0} sub={isPaid ? 'Paid access customers' : 'Hidden for free episodes'} color="#8b5cf6" />
        <StatCard title="Episode Duration" value={detail?.duration || '—'} sub={`Status: ${detail?.status || '—'}`} color="#ec4899" />
      </div>

      <div className="ep-table-card">
        <div className="ep-table-head">
          <div>
            <h3>Watch Customer List</h3>
            <p>All customers who watched this episode</p>
          </div>
          <TableControls rows={watchRows} setRows={setWatchRows} />
        </div>

        <TableWrapper loading={watchLoading} empty={!watchers.length} emptyText="No customer watch data found.">
          <table className="ep-table">
            <thead>
              <tr>
                <th>Customer</th>
                <th>Customer ID</th>
                <th>Email</th>
                <th>Mobile</th>
                <th>Watch Time</th>
              </tr>
            </thead>
            <tbody>
              {watchers.map((user) => (
                <tr key={user.id}>
                  <td>
                    <div className="ep-user-cell">
                      <div className="ep-avatar">{initials(user)}</div>
                      <div>
                        <div className="ep-user-name">{displayName(user)}</div>
                        <div className="ep-user-sub">Viewer</div>
                      </div>
                    </div>
                  </td>
                  <td>#{user.id}</td>
                  <td>{user.email || '—'}</td>
                  <td>{user.mobileNumber || '—'}</td>
                  <td>{formatDate(user.watch_timing)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrapper>

        <Pagination
          page={watchPage}
          setPage={setWatchPage}
          totalPages={totalWatchPages}
          totalCount={watchCount}
          rows={watchRows}
        />
      </div>

      {isPaid && (
        <div className="ep-table-card">
          <div className="ep-table-head">
            <div>
              <h3>Coin Unlock Customer List</h3>
              <p>Customers who used coins to watch this paid episode</p>
            </div>
            <TableControls rows={coinRows} setRows={setCoinRows} />
          </div>

          <TableWrapper loading={coinLoading} empty={!coinUsers.length} emptyText="No coin usage found for this episode.">
            <table className="ep-table">
              <thead>
                <tr>
                  <th>Customer</th>
                  <th>Customer ID</th>
                  <th>Email</th>
                  <th>Mobile</th>
                  <th>Coin Watch Time</th>
                </tr>
              </thead>
              <tbody>
                {coinUsers.map((user) => (
                  <tr key={user.id}>
                    <td>
                      <div className="ep-user-cell">
                        <div className="ep-avatar coin">{initials(user)}</div>
                        <div>
                          <div className="ep-user-name">{displayName(user)}</div>
                          <div className="ep-user-sub">Coin unlock</div>
                        </div>
                      </div>
                    </td>
                    <td>#{user.id}</td>
                    <td>{user.email || '—'}</td>
                    <td>{user.mobileNumber || '—'}</td>
                    <td>{formatDate(user.watch_timing)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </TableWrapper>

          <Pagination
            page={coinPage}
            setPage={setCoinPage}
            totalPages={totalCoinPages}
            totalCount={coinCount}
            rows={coinRows}
          />
        </div>
      )}
    </div>
  );
};

const Info = ({ label, value, loading }) => (
  <div className="ep-info-item">
    <div className="ep-info-label">{label}</div>
    <div className="ep-info-value">{loading ? <div className="ep-skeleton sm" /> : (value || '—')}</div>
  </div>
);

const StatCard = ({ title, value, sub, color }) => (
  <div className="ep-stat-card" style={{ borderTopColor: color }}>
    <div className="ep-stat-title">{title}</div>
    <div className="ep-stat-value">{value}</div>
    <div className="ep-stat-sub">{sub}</div>
  </div>
);

const TableControls = ({ rows, setRows }) => (
  <div className="ep-table-controls">
    <span>Rows:</span>
    <select value={rows} onChange={(e) => setRows(Number(e.target.value))} className="ep-select">
      {ROW_OPTIONS.map((r) => <option key={r} value={r}>{r}</option>)}
    </select>
  </div>
);

const TableWrapper = ({ children, loading, empty, emptyText }) => {
  if (loading) {
    return (
      <div className="ep-loading-wrap">
        {[1,2,3,4,5].map((i) => <div key={i} className="ep-skeleton row" />)}
      </div>
    );
  }
  if (empty) {
    return <div className="ep-empty-state">{emptyText}</div>;
  }
  return <div className="ep-table-wrap">{children}</div>;
};

const Pagination = ({ page, setPage, totalPages, totalCount, rows }) => {
  const start = totalCount === 0 ? 0 : (page - 1) * rows + 1;
  const end = Math.min(page * rows, totalCount);
  return (
    <div className="ep-pagination">
      <div className="ep-pagination-info">Showing {start}-{end} of {totalCount}</div>
      <div className="ep-pagination-actions">
        <button className="ep-page-btn" disabled={page <= 1} onClick={() => setPage(page - 1)}>Prev</button>
        <div className="ep-page-current">{page}</div>
        <button className="ep-page-btn" disabled={page >= totalPages} onClick={() => setPage(page + 1)}>Next</button>
      </div>
    </div>
  );
};

const styles = `
.ep-page-root{padding:24px;background:var(--themeColorLighterShade);min-height:100vh;color:var(--themeFontColor);font-family:Inter,system-ui,sans-serif;display:flex;flex-direction:column;gap:18px}
.ep-topbar{display:flex;align-items:flex-start;justify-content:space-between;gap:16px;flex-wrap:wrap}
.ep-page-title{margin:0;font-size:1.6rem;font-weight:800;color:var(--themeFontColor)}
.ep-page-sub{margin:4px 0 0;font-size:.9rem;opacity:.62}
.ep-back-btn{height:42px;padding:0 16px;border:1px solid var(--formBorderLineColor);background:var(--themeColor);color:var(--themeFontColor);border-radius:10px;display:flex;align-items:center;gap:8px;cursor:pointer;box-shadow:var(--themeShadow)}
.ep-back-btn:hover{border-color:#6366f1;color:#6366f1}
.ep-hero-grid{display:grid;grid-template-columns:360px 1fr;gap:18px}
.ep-side-card,.ep-main-card,.ep-table-card,.ep-stat-card{background:var(--themeColor);border:1px solid var(--formBorderLineColor);border-radius:18px;box-shadow:var(--themeShadow)}
.ep-thumb-block{padding:22px}
.ep-thumb-inner{height:260px;border-radius:16px;background:linear-gradient(135deg,#1f3a8a 0%,#7c3aed 100%);display:flex;flex-direction:column;justify-content:flex-end;padding:20px;position:relative;overflow:hidden}
.ep-thumb-inner:before{content:'';position:absolute;inset:0;background:radial-gradient(circle at top right,rgba(255,255,255,.18),transparent 35%)}
.ep-episode-no{position:absolute;top:16px;left:16px;padding:7px 12px;border-radius:999px;background:rgba(0,0,0,.28);font-size:.78rem;font-weight:700;color:#fff;backdrop-filter:blur(6px)}
.ep-thumb-title{position:relative;font-size:1.75rem;font-weight:800;color:#fff;line-height:1.1;max-width:220px}
.ep-access-pill{position:relative;display:inline-flex;width:max-content;margin-top:12px;padding:8px 12px;border-radius:999px;font-size:.8rem;font-weight:700;color:#fff}
.ep-access-pill.free{background:#059669}
.ep-access-pill.paid{background:#d97706}
.ep-kpi-box{margin:0 22px 22px;padding:18px;border-radius:14px;background:var(--themeColorLightestShade);text-align:center}
.ep-kpi-label{font-size:.85rem;opacity:.62;margin-bottom:6px}
.ep-kpi-value{font-size:2rem;font-weight:800;color:var(--themeFontColor)}
.ep-main-head{padding:22px 22px 18px;background:linear-gradient(90deg,#818cf8,#a855f7);color:#fff;font-size:1.1rem;font-weight:800;border-radius:18px 18px 0 0}
.ep-main-body{padding:22px}
.ep-info-grid{display:grid;grid-template-columns:repeat(2,minmax(0,1fr));gap:20px 28px}
.ep-info-item{min-height:52px}
.ep-info-label{font-size:.78rem;opacity:.62;margin-bottom:5px}
.ep-info-value{font-size:.95rem;font-weight:600;word-break:break-word;color:var(--themeFontColor)}
.ep-status{display:inline-flex;padding:6px 12px;border-radius:999px;font-size:.8rem;font-weight:700}
.ep-status.active{background:rgba(16,185,129,.16);color:#10b981}
.ep-status.inactive{background:rgba(239,68,68,.16);color:#ef4444}
.ep-link{color:#60a5fa;text-decoration:none;font-weight:700}
.ep-link:hover{text-decoration:underline}
.ep-stats-row{display:grid;grid-template-columns:repeat(4,minmax(0,1fr));gap:16px}
.ep-stat-card{padding:0;overflow:hidden;border-top:3px solid}
.ep-stat-top{display:none}
.ep-stat-title{padding:16px 18px 6px;font-size:.78rem;text-transform:uppercase;letter-spacing:.05em;opacity:.6;font-weight:700}
.ep-stat-value{padding:0 18px;font-size:1.6rem;font-weight:800;color:var(--themeFontColor)}
.ep-stat-sub{padding:6px 18px 16px;font-size:.8rem;opacity:.55}
.ep-table-head{padding:18px 20px;border-bottom:1px solid var(--formBorderLineColor);display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
.ep-table-head h3{margin:0;font-size:1rem;font-weight:800;color:var(--themeFontColor)}
.ep-table-head p{margin:4px 0 0;font-size:.82rem;opacity:.58}
.ep-table-controls{display:flex;align-items:center;gap:10px;font-size:.82rem;opacity:.8}
.ep-select{height:38px;padding:0 12px;border-radius:10px;border:1px solid var(--formBorderLineColor);background:var(--themeColorLighterShade);color:var(--themeFontColor);outline:none}
.ep-table-wrap{overflow:auto}
.ep-table{width:100%;border-collapse:collapse;min-width:850px}
.ep-table th{padding:14px 18px;text-align:left;font-size:.76rem;text-transform:uppercase;letter-spacing:.05em;opacity:.55;background:var(--themeColorLighterShade);border-bottom:1px solid var(--formBorderLineColor)}
.ep-table td{padding:14px 18px;border-bottom:1px solid var(--formBorderLineColor);font-size:.88rem;vertical-align:middle}
.ep-table tbody tr:hover{background:rgba(99,102,241,.05)}
.ep-user-cell{display:flex;align-items:center;gap:12px}
.ep-avatar{width:38px;height:38px;border-radius:50%;display:flex;align-items:center;justify-content:center;background:linear-gradient(135deg,#3b82f6,#6366f1);color:#fff;font-size:.8rem;font-weight:800;flex-shrink:0}
.ep-avatar.coin{background:linear-gradient(135deg,#f59e0b,#8b5cf6)}
.ep-user-name{font-weight:700;color:var(--themeFontColor)}
.ep-user-sub{font-size:.75rem;opacity:.5}
.ep-pagination{padding:16px 20px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
.ep-pagination-info{font-size:.82rem;opacity:.62}
.ep-pagination-actions{display:flex;align-items:center;gap:10px}
.ep-page-btn{height:38px;padding:0 14px;border-radius:10px;border:1px solid var(--formBorderLineColor);background:var(--themeColorLighterShade);color:var(--themeFontColor);cursor:pointer}
.ep-page-btn:hover:not(:disabled){border-color:#6366f1;color:#6366f1}
.ep-page-btn:disabled{opacity:.38;cursor:not-allowed}
.ep-page-current{min-width:38px;height:38px;padding:0 12px;border-radius:10px;background:linear-gradient(135deg,#6366f1,#a855f7);color:#fff;display:flex;align-items:center;justify-content:center;font-weight:800}
.ep-loading-wrap{padding:18px}
.ep-skeleton{background:linear-gradient(90deg,var(--themeColorLightestShade) 25%,var(--themeColorLighterShade) 50%,var(--themeColorLightestShade) 75%);background-size:200% 100%;animation:epShimmer 1.4s infinite ease-in-out;border-radius:10px}
.ep-skeleton.row{height:54px;margin-bottom:12px}
.ep-skeleton.sm{height:16px;width:70%}
.ep-empty-state{padding:34px;text-align:center;opacity:.62}
@keyframes epShimmer{0%{background-position:-200% 0}100%{background-position:200% 0}}
@media (max-width:1100px){.ep-hero-grid{grid-template-columns:1fr}.ep-stats-row{grid-template-columns:repeat(2,minmax(0,1fr))}}
@media (max-width:700px){.ep-page-root{padding:16px}.ep-info-grid{grid-template-columns:1fr}.ep-stats-row{grid-template-columns:1fr}.ep-thumb-inner{height:220px}.ep-page-title{font-size:1.35rem}}
`;

const mapDispatch = (dispatch) => bindActionCreators({
  episode_detail_admin,
  episode_watch_by_customer,
  episode_watch_by_coin_customer_list,
}, dispatch);

export default connect(null, mapDispatch)(EpisodeDetail);
